import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Calendar, RefreshCw, GripVertical } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { analyticsAPI } from '../../utils/api';
import { getErrorMessage } from '../../utils/errorTranslation';

import CandidatesMetrics from './widgets/CandidatesMetrics';
import OffersMetrics from './widgets/OffersMetrics';
import AIMetrics from './widgets/AIMetrics';
import QualityMetrics from './widgets/QualityMetrics';
import HistoryMetrics from './widgets/HistoryMetrics';

const DEFAULT_ORDER = [
  'candidates-total',
  'offers-active',
  'ai-analyses',
  'quality-score',
  'activity-recent'
];

const parseJsonSafe = (value, fallback) => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }
  return value;
};

const StrictModeDroppable = ({ children, ...props }) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animationFrame = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animationFrame);
      setEnabled(false);
    };
  }, []);

  if (!enabled) return null;
  return <Droppable {...props}>{children}</Droppable>;
};

const AnalyticsView = ({ showToast }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    period: '7days',
    offerId: null
  });
  const [widgetOrder, setWidgetOrder] = useState(DEFAULT_ORDER);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const response = await analyticsAPI.getPreferences();
      const data = response.data?.data || {};
      const parsedOrder = parseJsonSafe(data.widget_order, DEFAULT_ORDER);
      const parsedFilters = parseJsonSafe(data.filters, {});
      const order = Array.isArray(parsedOrder) && parsedOrder.length
        ? parsedOrder
        : DEFAULT_ORDER;
      setWidgetOrder(order);
      setFilters(prev => ({ ...prev, ...(parsedFilters || {}) }));
    } catch (error) {
      console.error('Error fetching preferences:', error);
      setWidgetOrder(DEFAULT_ORDER);
      if (showToast) {
        showToast(getErrorMessage(error, t, t('dashboard.errors.preferencesLoadFailed', 'Error loading preferences')), 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async (updates) => {
    try {
      await analyticsAPI.updatePreferences(updates);
      return true;
    } catch (error) {
      console.error('Error saving preferences:', error);
      if (showToast) {
        showToast(getErrorMessage(error, t, t('dashboard.errors.preferencesSaveFailed', 'Error saving preferences')), 'error');
      }
      return false;
    }
  };

  const handleFilterChange = (newFilters) => {
    const next = { ...filters, ...newFilters };
    setFilters(next);
    savePreferences({ filters: next });
  };

  const handleRefresh = () => {
    fetchPreferences();
    if (showToast) {
      showToast(t('dashboard.analytics.export.inProgress', 'Actualisation...'), 'info');
    }
  };

  const handleOnDragEnd = async (result) => {
    if (!result.destination) return;

    const previousOrder = Array.from(widgetOrder);
    const items = Array.from(widgetOrder);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setWidgetOrder(items);
    const saved = await savePreferences({ widget_order: items });
    if (!saved) {
      setWidgetOrder(previousOrder);
      return;
    }
    if (saved && showToast) {
      showToast(t('dashboard.analytics.dragDrop.reorderSuccess', 'Widgets réorganisés'), 'success');
    }
  };

  const widgetComponents = {
    'candidates-total': <CandidatesMetrics filters={filters} showToast={showToast} />,
    'offers-active': <OffersMetrics filters={filters} showToast={showToast} />,
    'ai-analyses': <AIMetrics filters={filters} showToast={showToast} />,
    'quality-score': <QualityMetrics filters={filters} showToast={showToast} />,
    'activity-recent': <HistoryMetrics filters={filters} showToast={showToast} />
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters Bar */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-gray-400" />
            <select
              value={filters.period}
              onChange={(e) => handleFilterChange({ period: e.target.value })}
              className="text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="7days">{t('dashboard.analytics.filters.last7Days')}</option>
              <option value="30days">{t('dashboard.analytics.filters.last30Days')}</option>
              <option value="90days">{t('dashboard.analytics.filters.last90Days')}</option>
            </select>
          </div>

          <button
            onClick={handleRefresh}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
            type="button"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('common.refresh', 'Actualiser')}
          </button>
        </div>
      </div>

      {/* Widgets */}
      {widgetOrder.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 text-center text-gray-500">
          <p className="font-medium">{t('dashboard.analytics.empty.title')}</p>
          <p className="text-sm mt-1">{t('dashboard.analytics.empty.description')}</p>
        </div>
      ) : (
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <StrictModeDroppable droppableId="analytics-widgets">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="grid grid-cols-1 lg:grid-cols-2 lg:auto-rows-fr gap-6"
              >
                {widgetOrder.map((widgetId, index) => (
                  <Draggable key={widgetId} draggableId={widgetId} index={index}>
                    {(dragProvided, snapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        style={dragProvided.draggableProps.style}
                        className={`relative h-full ${snapshot.isDragging ? 'ring-2 ring-primary-300 rounded-lg' : ''}`}
                      >
                        <div
                          {...dragProvided.dragHandleProps}
                          className="absolute top-3 right-3 z-10 text-gray-400 cursor-grab active:cursor-grabbing p-1 rounded hover:bg-gray-100"
                          title={t('dashboard.analytics.dragDrop.hint', 'Drag to reorder')}
                        >
                          <GripVertical className="h-5 w-5" />
                        </div>
                        {widgetComponents[widgetId] || null}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </StrictModeDroppable>
        </DragDropContext>
      )}
    </div>
  );
};

export default AnalyticsView;
